import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useCompany } from "../hooks/useCompany";
import { download1099PDF } from "../components/Form1099PDF";

const API_URL = process.env.REACT_APP_API_URL;

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

const fmtAmount = (n) =>
  Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Converts a DB Filing1099 record back to the shape fillPDFBytes expects. */
const filingToFormData = (f) => ({
  taxYear: f.tax_year,
  payerLine1: f.payer_line1 || "",
  payerLine2: f.payer_line2 || "",
  payerLine3: [
    [f.payer_city, f.payer_state].filter(Boolean).join(", "),
    f.payer_zip,
    f.payer_phone,
  ].filter(Boolean).join("  "),
  payerTIN: f.payer_tin || "",
  recipientName: f.recipient_name || "",
  recipientAddress: f.recipient_address || "",
  recipientCityLine: [
    [f.recipient_city, f.recipient_state].filter(Boolean).join(", "),
    f.recipient_zip,
  ].filter(Boolean).join("  "),
  recipientTIN: "", // not stored
  box1: fmtAmount(f.box1),
  box4: fmtAmount(f.box4),
});

const Filings1099List = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activeCompany } = useCompany();

  const [taxYear, setTaxYear] = useState(String(currentYear - 1));
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null); // filing_id being downloaded
  const [sort, setSort] = useState({ key: "recipient_name", dir: "asc" });

  useEffect(() => {
    if (!auth?.isAuthenticated || !activeCompany) return;
    setLoading(true);
    fetch(
      `${API_URL}/filings_1099?company_id=${activeCompany.company_id}&tax_year=${taxYear}`,
      { headers: { Authorization: `Bearer ${auth.user?.access_token}` } }
    )
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => [])
      .then((data) => {
        setFilings(Array.isArray(data) ? data : []);
        setLoading(false);
      });
  }, [auth.isAuthenticated, auth.user?.access_token, activeCompany, taxYear]);

  const handleRedownload = async (f) => {
    setDownloading(f.filing_id);
    try {
      await download1099PDF(filingToFormData(f));
    } finally {
      setDownloading(null);
    }
  };

  const toggleSort = (key) =>
    setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));

  const sortedFilings = useMemo(() => {
    const { key, dir } = sort;
    return [...filings].sort((a, b) => {
      let av = a[key] ?? "", bv = b[key] ?? "";
      if (key === "box1" || key === "box4") return dir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
      if (key === "updated_at") return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      return dir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [filings, sort]);

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const handleSignOut = () => {
    auth.signOut();
  };

  const totalCompensation = filings.reduce((s, f) => s + (parseFloat(f.box1) || 0), 0);
  const totalWithheld = filings.reduce((s, f) => s + (parseFloat(f.box4) || 0), 0);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
        <div className="flex flex-col flex-1 p-3 sm:p-6">
          <div className="bg-white shadow-lg rounded-lg p-4 sm:p-8 w-full">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-1">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">1099-NEC History</h1>
              <button
                onClick={() => navigate("/Generate1099")}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition text-sm font-medium w-full sm:w-auto"
              >
                + New 1099
              </button>
            </div>
            <p className="text-gray-500 text-sm mb-6">
              All saved 1099-NEC filings for the selected tax year.
            </p>

            {/* Year filter */}
            <div className="mb-6 flex items-center gap-3">
              <label className="text-sm font-medium text-gray-600">Tax Year</label>
              <select
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {/* Summary stats */}
            {!loading && filings.length > 0 && (
              <div className="mb-6 flex flex-wrap gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 min-w-[130px]">
                  <p className="text-blue-700 font-medium text-xs">Recipients</p>
                  <p className="text-xl font-bold text-blue-800">{filings.length}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 min-w-[160px]">
                  <p className="text-green-700 font-medium text-xs">Total Compensation</p>
                  <p className="text-xl font-bold text-green-800">${fmtAmount(totalCompensation)}</p>
                </div>
                {totalWithheld > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 min-w-[160px]">
                    <p className="text-yellow-700 font-medium text-xs">Total Withheld</p>
                    <p className="text-xl font-bold text-yellow-800">${fmtAmount(totalWithheld)}</p>
                  </div>
                )}
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-gray-400 text-sm">Loading 1099 filings…</span>
              </div>
            ) : filings.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-400 text-lg mb-4">No 1099s saved for {taxYear}.</p>
                <button
                  onClick={() => navigate("/Generate1099")}
                  className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 text-sm"
                >
                  Generate a 1099
                </button>
              </div>
            ) : (
              <>
                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-300 text-sm">
                    <thead>
                      <tr className="bg-gray-200 text-gray-700">
                        <th className="px-4 py-2 border text-left cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("recipient_name")}>Recipient{sortIcon("recipient_name")}</th>
                        <th className="px-4 py-2 border text-left whitespace-nowrap">Address</th>
                        <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("box1")}>Box 1 (Compensation){sortIcon("box1")}</th>
                        <th className="px-4 py-2 border text-right cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("box4")}>Box 4 (Withheld){sortIcon("box4")}</th>
                        <th className="px-4 py-2 border text-center cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort("updated_at")}>Last Updated{sortIcon("updated_at")}</th>
                        <th className="px-4 py-2 border text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFilings.map((f) => (
                        <tr key={f.filing_id} className="hover:bg-gray-50 transition">
                          <td className="px-4 py-2 border font-medium text-gray-800">{f.recipient_name || <span className="text-gray-400 italic">—</span>}</td>
                          <td className="px-4 py-2 border text-gray-500 text-xs">{[f.recipient_address, f.recipient_city, f.recipient_state, f.recipient_zip].filter(Boolean).join(", ") || "—"}</td>
                          <td className="px-4 py-2 border text-right font-medium text-gray-800">${fmtAmount(f.box1)}</td>
                          <td className="px-4 py-2 border text-right text-gray-600">{parseFloat(f.box4) > 0 ? `$${fmtAmount(f.box4)}` : "—"}</td>
                          <td className="px-4 py-2 border text-center text-xs text-gray-500 whitespace-nowrap">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}</td>
                          <td className="px-4 py-2 border text-center">
                            <button onClick={() => handleRedownload(f)} disabled={downloading === f.filing_id} className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 text-xs disabled:opacity-50">
                              {downloading === f.filing_id ? "…" : "↓ PDF"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden space-y-3">
                  {sortedFilings.map((f) => (
                    <div key={f.filing_id} className="border border-gray-200 rounded-lg p-3 bg-white">
                      <div className="flex items-start justify-between mb-1">
                        <div className="flex-1 min-w-0 mr-3">
                          <p className="font-semibold text-gray-800">{f.recipient_name || "—"}</p>
                          <p className="text-xs text-gray-500 truncate">{[f.recipient_address, f.recipient_city, f.recipient_state].filter(Boolean).join(", ") || "—"}</p>
                        </div>
                        <button onClick={() => handleRedownload(f)} disabled={downloading === f.filing_id} className="bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 text-xs disabled:opacity-50 flex-shrink-0">
                          {downloading === f.filing_id ? "…" : "↓ PDF"}
                        </button>
                      </div>
                      <div className="flex justify-between text-xs mt-2 pt-2 border-t border-gray-100">
                        <span className="text-gray-500">Box 1: <span className="font-semibold text-gray-800">${fmtAmount(f.box1)}</span></span>
                        {parseFloat(f.box4) > 0 && <span className="text-gray-500">Withheld: <span className="font-semibold">${fmtAmount(f.box4)}</span></span>}
                        <span className="text-gray-400">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="mt-6">
              <button
                onClick={() => navigate("/")}
                className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition text-sm"
              >
                {t("back_to_dashboard_btn")}
              </button>
            </div>

          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default Filings1099List;
